/**
 * Weather data types as TLS DE-Typ code.
 *
 * Values and order mirror `WeatherDetyp` of the API, which delivers the type as
 * numeric `valueType` — for schema, details and history alike.
 */
export enum WeatherDataTypeEnum {
  AirTemperature      = 48,
  SurfaceTemperature  = 49,
  DewTemperature      = 66,
  RelativeHumidity    = 55,
  Grip                = 77,
  SnowHeight          = 58,
  AirPressure         = 54,
  Visibility          = 60,
  SurfaceStateDin     = 79,
  PrecipitationKind   = 71,
  RainIntensity       = 53,
  WindDirection       = 56,
  WindSpeedMean       = 57,
  WindSpeedMax        = 64,
  WaterLayerThickness = 72,
  IceLayerThickness   = 76,
  SnowLayerThickness  = 75,
  SurfaceState        = 70,
}

/** Color for data types not known here — see `resolveWeatherDataType`. */
export const weatherUnknownTypeChartColor = '#6c757d';

/** Enriched weather data value for station display */
export interface WeatherDataTypeDisplay {
  /** Name/Label für the data type: Tooltip */
  label: string;
  /** unit string */
  unit: string;
  /** pth to the icon for this data type */
  icon: string;
  /** number of decimals for formatting */
  decimalPlaces: number;
  /** Icon is rotated by value — just used for wind direction. */
  isIconRotatedByValue?: boolean;
  /** Line color in the history chart, fixed per data type. */
  chartColor: string;
}

export const weatherIconDirectory = 'assets/icons/weather/';

export const weatherDataTypeDisplays: Record<WeatherDataTypeEnum, WeatherDataTypeDisplay> = {
  [WeatherDataTypeEnum.AirTemperature]:      { label: 'Lufttemperatur',                 unit: '°C',   icon: 'thermostat.svg',          decimalPlaces: 1, chartColor: '#c74b47' },
  [WeatherDataTypeEnum.SurfaceTemperature]:  { label: 'Temperatur Oberfläche',          unit: '°C',   icon: 'heat.svg',                decimalPlaces: 1, chartColor: '#22b27e' },
  [WeatherDataTypeEnum.DewTemperature]:      { label: 'Taupunkttemperatur',             unit: '°C',   icon: 'dew_point.svg',           decimalPlaces: 1, chartColor: '#9489e9' },
  [WeatherDataTypeEnum.RelativeHumidity]:    { label: 'Relative Luftfeuchtigkeit',      unit: '%',    icon: 'humidity_percentage.svg', decimalPlaces: 0, chartColor: '#0085ca' },
  [WeatherDataTypeEnum.Grip]:                { label: 'Griffigkeit',                    unit: '',     icon: 'tire_repair.svg',         decimalPlaces: 2, chartColor: '#8a5fc9' },
  [WeatherDataTypeEnum.SnowHeight]:          { label: 'Schneehöhe',                     unit: 'mm',   icon: 'weather_snowy.svg',       decimalPlaces: 0, chartColor: '#b09802' },
  [WeatherDataTypeEnum.AirPressure]:         { label: 'Luftdruck',                      unit: 'hPa',  icon: 'compress.svg',            decimalPlaces: 0, chartColor: '#008fb5' },
  [WeatherDataTypeEnum.Visibility]:          { label: 'Sichtweite',                     unit: 'm',    icon: 'visibility.svg',          decimalPlaces: 0, chartColor: '#c78b0a' },
  [WeatherDataTypeEnum.SurfaceStateDin]:     { label: 'Oberflächenzustand (DIN)',       unit: '',     icon: 'road.svg',                decimalPlaces: 0, chartColor: '#598c00' },
  [WeatherDataTypeEnum.PrecipitationKind]:   { label: 'Niederschlagsart',               unit: '',     icon: 'rainy.svg',               decimalPlaces: 0, chartColor: '#e07080' },
  [WeatherDataTypeEnum.RainIntensity]:       { label: 'Regenintensität',                unit: 'mm',   icon: 'rainy_heavy.svg',         decimalPlaces: 1, chartColor: '#a355b4' },
  [WeatherDataTypeEnum.WindDirection]:       { label: 'Windrichtung',                   unit: '°',    icon: 'navigation.svg',          decimalPlaces: 0, isIconRotatedByValue: true, chartColor: '#b86000' },
  [WeatherDataTypeEnum.WindSpeedMean]:       { label: 'Wind Geschwindigkeit (Mittel)',  unit: 'm/s',  icon: 'air.svg',                 decimalPlaces: 1, chartColor: '#009343' },
  [WeatherDataTypeEnum.WaterLayerThickness]: { label: 'Wasserschichtdicke',             unit: 'mm',   icon: 'water.svg',               decimalPlaces: 2, chartColor: '#c4530e' },
  [WeatherDataTypeEnum.WindSpeedMax]:        { label: 'Wind Geschwindigkeit (Spitze)',  unit: 'm/s',  icon: 'storm.svg',               decimalPlaces: 1, chartColor: '#d871a0' },
  [WeatherDataTypeEnum.IceLayerThickness]:   { label: 'Eisschichtdicke',                unit: 'mm',   icon: 'ac_unit.svg',             decimalPlaces: 2, chartColor: '#00b3a0' },
  [WeatherDataTypeEnum.SnowLayerThickness]:  { label: 'Schneeschichtdicke',             unit: 'mm',   icon: 'snowing.svg',             decimalPlaces: 1, chartColor: '#7293ee' },
  [WeatherDataTypeEnum.SurfaceState]:        { label: 'Oberflächenzustand',             unit: '',     icon: 'road.svg',                decimalPlaces: 0, chartColor: '#00b0be' },
};

/**
 * Resolves a `valueType` of the API to the known data type.
 *
 * Returns `undefined` for data types that this portal version does not know yet
 * (forwards compatibility: the API may deliver additional DE-Typen).
 */
export function resolveWeatherDataType(valueType: number): WeatherDataTypeEnum | undefined {
  return valueType in weatherDataTypeDisplays ? valueType as WeatherDataTypeEnum : undefined;
}

/** A history chart: all data types of one unit share a y-axis. */
export interface WeatherChartGroup {
  /** Heading above the chart. */
  title: string;
  /** Label of the y-axis, unit of all data types of the chart. */
  axisLabel: string;
  /** Data types of the chart, in the order of their series and legend entries. */
  types: WeatherDataTypeEnum[];
}

/**
 * The history charts: one per unit, since values of different units cannot
 * share a y-axis. The order determines the order of the charts in the dialog.
 *
 * Every data type belongs to exactly one chart — a new data type has to be
 * added here, otherwise it does not appear in the history. Which of the charts
 * actually gets drawn depends on the data types the API delivers.
 *
 * The `chartColor`s are validated against this grouping (distinguishable also
 * for color vision deficiency), so moving a data type to another chart requires
 * a re-check of the colors.
 */
export const weatherChartGroups: WeatherChartGroup[] = [
  {
    title: 'Temperaturen',
    axisLabel: 'Temperatur [°C]',
    types: [
      WeatherDataTypeEnum.AirTemperature,
      WeatherDataTypeEnum.SurfaceTemperature,
      WeatherDataTypeEnum.DewTemperature,
    ]
  },
  {
    title: 'Luftfeuchtigkeit',
    axisLabel: 'Rel. Luftfeuchtigkeit [%]',
    types: [WeatherDataTypeEnum.RelativeHumidity]
  },
  {
    title: 'Luftdruck',
    axisLabel: 'Luftdruck [hPa]',
    types: [WeatherDataTypeEnum.AirPressure]
  },
  {
    title: 'Windgeschwindigkeit',
    axisLabel: 'Geschwindigkeit [m/s]',
    types: [
      WeatherDataTypeEnum.WindSpeedMean,
      WeatherDataTypeEnum.WindSpeedMax,
    ]
  },
  {
    title: 'Windrichtung',
    axisLabel: 'Windrichtung [°]',
    types: [WeatherDataTypeEnum.WindDirection]
  },
  {
    title: 'Niederschlag und Schichtdicken',
    axisLabel: 'Höhe bzw. Dicke [mm]',
    types: [
      WeatherDataTypeEnum.RainIntensity,
      WeatherDataTypeEnum.SnowHeight,
      WeatherDataTypeEnum.WaterLayerThickness,
      WeatherDataTypeEnum.IceLayerThickness,
      WeatherDataTypeEnum.SnowLayerThickness,
    ]
  },
  {
    title: 'Sichtweite',
    axisLabel: 'Sichtweite [m]',
    types: [WeatherDataTypeEnum.Visibility]
  },
  {
    title: 'Zustände und Griffigkeit',
    axisLabel: 'Wert',
    types: [
      WeatherDataTypeEnum.PrecipitationKind,
      WeatherDataTypeEnum.SurfaceState,
      WeatherDataTypeEnum.SurfaceStateDin,
      WeatherDataTypeEnum.Grip,
    ]
  },
];